import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  userId: string;
  userEmail: string;
  relatedStepKey?: string | null;
  onClose: () => void;
  onSent: () => void;
}

export function PersonalMailModal({ userId, userEmail, relatedStepKey, onClose, onSent }: Props) {
  const auth = useAuth() as any;
  const { showToast } = useToast();
  const senderFirstName = auth.user?.first_name || 'Dibbi';
  const senderAuthId = auth.authUser?.id ?? '00000000-0000-0000-0000-000000000000';
  const [subject, setSubject] = useState('Even bij je inchecken');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  async function send() {
    if (!body.trim()) {
      showToast('Body mag niet leeg zijn', 'error');
      return;
    }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-journey-personal-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
          user_id: userId,
          subject,
          body,
          sender_first_name: senderFirstName,
          sender_auth_user_id: senderAuthId,
          related_step_key: relatedStepKey ?? null,
        }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      showToast('Mail verzonden', 'success');
      onSent();
      onClose();
    } catch (e: any) {
      showToast('Verzenden mislukt', 'error', e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold">Persoonlijke mail naar {userEmail}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-500">Verzonden vanaf <code>support@dibbi.nl</code> namens <code>{senderFirstName} via Dibbi</code></div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Onderwerp</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bericht (plain text)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={10} className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" placeholder={'Hoi,\n\n...\n\nGroet,\n' + senderFirstName} />
          </div>
          {relatedStepKey && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Verzenden voltooit stap <code>{relatedStepKey}</code> automatisch en plant de volgende.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Annuleren</button>
          <button onClick={send} disabled={sending} className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded disabled:opacity-50">
            {sending ? 'Verzenden...' : 'Verstuur'}
          </button>
        </div>
      </div>
    </div>
  );
}
